/**
 * Phase 10 R1 — Shop Labor Rates Hook
 *
 * ⚠️  CONCEPT BOUNDARY (do not collapse):
 *   Labor sell rate ≠ tech pay rate ≠ labor cost.
 *   This hook returns only the **applied labor sell rate** inputs configured
 *   at the shop level. Tech pay (R3) and GP cost (R4) are owned by later rounds.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { ShopLaborRates } from '@/lib/labor/resolveLaborRate';

export interface ShopLaborRatesRow extends ShopLaborRates {
  shop_id: string;
}

export function useShopLaborRates() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id ?? null;

  return useQuery({
    queryKey: ['shop-labor-rates', shopId],
    enabled: !!shopId,
    queryFn: async (): Promise<ShopLaborRatesRow | null> => {
      if (!shopId) return null;
      const { data, error } = await supabase
        .from('shops')
        .select('id, default_labor_rate, diagnostic_labor_rate, maintenance_labor_rate')
        .eq('id', shopId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        shop_id: data.id,
        default_labor_rate: Number(data.default_labor_rate ?? 0),
        diagnostic_labor_rate:
          data.diagnostic_labor_rate != null ? Number(data.diagnostic_labor_rate) : null,
        maintenance_labor_rate:
          data.maintenance_labor_rate != null ? Number(data.maintenance_labor_rate) : null,
      };
    },
  });
}
